import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Users,
  FileText,
  MapPin,
  Search,
  Filter,
  ArrowLeft,
  Calendar,
  Mail,
  Phone,
  Home,
  Download,
  Loader2,
  Pencil,
  Trash2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const extensionAreas = [
  "Ennerdale",
  "Lenasia",
  "Toekomsrus",
  "Lehae",
  "Klipspruit West",
  "Kliptown",
  "Ext. 9",
  "Ext. 8",
  "Ext. 7",
  "Ext. 6",
  "Old Eldos",
  "Mahala Park",
  "Ext. 1",
  "Bushkoppies",
  "Ext. 3",
  "Ext. 2",
  "Ext. 4",
  "Freedom Park",
  "Devland",
  "Naturena",
  "Mondeor and South",
  "East Rand",
];

export const Dashboard = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterArea, setFilterArea] = useState("all");
  const [filterGender, setFilterGender] = useState("all");
  const [isExporting, setIsExporting] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [deletingRecord, setDeletingRecord] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editFormData, setEditFormData] = useState({});

  const fetchSubmissions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterArea && filterArea !== "all") params.append("extension_area", filterArea);
      if (filterGender && filterGender !== "all") params.append("gender", filterGender);
      if (searchTerm) params.append("search", searchTerm);

      const response = await axios.get(`${API}/submissions?${params}`);
      setSubmissions(response.data);
    } catch (error) {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, [filterArea, filterGender, searchTerm]); // Dependencies that fetchSubmissions uses

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/submissions/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to load stats:", error);
    }
  }, []); // No dependencies since it doesn't use any props/state

  useEffect(() => {
    fetchSubmissions();
    fetchStats();
  }, [fetchSubmissions, fetchStats]);

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterArea("all");
    setFilterGender("all");
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams();
      if (filterArea && filterArea !== "all") params.append("extension_area", filterArea);
      if (filterGender && filterGender !== "all") params.append("gender", filterGender);
      if (searchTerm) params.append("search", searchTerm);

      const response = await axios.get(`${API}/export/submissions?${params}`, {
        responseType: "blob",
      });

      // Extract filename from response header
      const contentDisposition = response.headers["content-disposition"];
      let filename = "fellowship_submissions.xlsx";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) filename = filenameMatch[1];
      }

      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Submissions exported successfully");
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("No submissions found to export. Adjust filters and try again.");
      } else {
        toast.error("Failed to export submissions");
      }
    } finally {
      setIsExporting(false);
    }
  };

  const handleEditClick = (record) => {
    setEditingRecord(record);
    setEditFormData({
      name: record.name,
      surname: record.surname,
      home_address: record.home_address,
      gender: record.gender,
      contact_number: record.contact_number,
      email: record.email,
      extension_area: record.extension_area,
    });
    setShowEditDialog(true);
  };

  const handleDeleteClick = (record) => {
    setDeletingRecord(record);
    setShowDeleteDialog(true);
  };

  const handleUpdateSubmission = async () => {
    if (!editingRecord) return;
    
    setIsUpdating(true);
    try {
      await axios.put(`${API}/submissions/${editingRecord.id}`, editFormData);
      toast.success("Submission updated successfully");
      setShowEditDialog(false);
      setEditingRecord(null);
      setEditFormData({});
      await fetchSubmissions();
      await fetchStats();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Submission not found");
      } else if (error.response?.status === 400) {
        toast.error(error.response.data.detail || "Invalid data");
      } else {
        toast.error("Failed to update submission");
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteSubmission = async () => {
    if (!deletingRecord) return;
    
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/submissions/${deletingRecord.id}`);
      toast.success("Submission deleted successfully");
      setShowDeleteDialog(false);
      setDeletingRecord(null);
      await fetchSubmissions();
      await fetchStats();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Submission not found");
      } else {
        toast.error("Failed to delete submission");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-stone-50 overflow-y-auto max-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-bold text-stone-900 mb-2"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                Fellowship Dashboard
              </h1>
              <p
                className="text-stone-600"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                Track and manage Right Hand of Fellowship candidates
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleExport}
                disabled={isExporting || submissions.length === 0}
                className="bg-green-600 hover:bg-green-700 text-white rounded-xl"
                data-testid="export-button"
              >
                {isExporting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Export to Excel
                  </>
                )}
              </Button>
              <Button
                onClick={() => navigate("/")}
                className="bg-[#063866] hover:bg-[#0B1B38] text-white rounded-xl"
                data-testid="back-to-form-button"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Form
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-stone-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-stone-600 flex items-center">
                  <Users className="mr-2 h-4 w-4" />
                  Total Candidates
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-[#063866]">
                  {stats.total_submissions}
                </div>
              </CardContent>
            </Card>

            <Card className="border-stone-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-stone-600 flex items-center">
                  <FileText className="mr-2 h-4 w-4" />
                  By Gender
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {Object.entries(stats.by_gender).map(([gender, count]) => (
                    <div
                      key={gender}
                      className="flex justify-between text-sm"
                    >
                      <span className="text-stone-600">{gender}:</span>
                      <span className="font-semibold text-stone-900">
                        {count}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-stone-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-stone-600 flex items-center">
                  <MapPin className="mr-2 h-4 w-4" />
                  Top Areas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {Object.entries(stats.by_area)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 3)
                    .map(([area, count]) => (
                      <div
                        key={area}
                        className="flex justify-between text-sm"
                      >
                        <span className="text-stone-600 truncate max-w-[150px]">
                          {area}:
                        </span>
                        <span className="font-semibold text-stone-900">
                          {count}
                        </span>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-6 pb-6">
        <Card className="border-stone-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <Filter className="mr-2 h-5 w-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="search">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-stone-400" />
                  <Input
                    id="search"
                    data-testid="search-input"
                    placeholder="Name, surname, email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 rounded-xl border-stone-200"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="filter-area">Extension/Area</Label>
                <Select 
                  value={filterArea} 
                  onValueChange={setFilterArea}
                >
                  <SelectTrigger
                    data-testid="filter-area-select"
                    className="rounded-xl border-stone-200"
                  >
                    <SelectValue placeholder="All areas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All areas</SelectItem>
                    {extensionAreas.map((area) => (
                      <SelectItem key={area} value={area}>
                        {area}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="filter-gender">Gender</Label>
                <Select 
                  value={filterGender} 
                  onValueChange={setFilterGender}
                >
                  <SelectTrigger
                    data-testid="filter-gender-select"
                    className="rounded-xl border-stone-200"
                  >
                    <SelectValue placeholder="All genders" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All genders</SelectItem>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>&nbsp;</Label>
                <Button
                  onClick={clearFilters}
                  variant="outline"
                  className="w-full rounded-xl border-stone-200"
                  data-testid="clear-filters-button"
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Submissions Table */}
      <div className="max-w-7xl mx-auto px-6 pb-12">
        <Card className="border-stone-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Submissions</CardTitle>
                <CardDescription>
                  {submissions.length} candidate
                  {submissions.length !== 1 ? "s" : ""} found
                </CardDescription>
              </div>
              {submissions.length > 0 && (
                <Button
                  onClick={handleExport}
                  disabled={isExporting}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="export-button-table"
                >
                  {isExporting ? (
                    <>
                      <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="mr-2 h-3 w-3" />
                      Export
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12 text-stone-500">
                Loading submissions...
              </div>
            ) : submissions.length === 0 ? (
              <div className="text-center py-12 text-stone-500">
                No submissions found. Adjust filters or add new candidates.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Gender</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Extension/Area</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {submissions.map((submission) => (
                      <TableRow key={submission.id} data-testid="submission-row">
                        <TableCell className="font-medium">
                          {submission.name} {submission.surname}
                        </TableCell>
                        <TableCell>{submission.gender}</TableCell>
                        <TableCell>
                          <div className="flex items-center text-sm">
                            <Phone className="mr-2 h-3 w-3 text-stone-400" />
                            {submission.contact_number}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center text-sm">
                            <Mail className="mr-2 h-3 w-3 text-stone-400" />
                            {submission.email}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center text-sm max-w-[200px] truncate">
                            <Home className="mr-2 h-3 w-3 text-stone-400 flex-shrink-0" />
                            {submission.home_address}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#063866]/10 text-[#0B1B38]">
                            {submission.extension_area}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center text-sm text-stone-500">
                            <Calendar className="mr-2 h-3 w-3" />
                            {formatDate(submission.timestamp)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              onClick={() => handleEditClick(submission)}
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600"
                              data-testid="edit-button"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => handleDeleteClick(submission)}
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600"
                              data-testid="delete-button"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => {
        if (!isUpdating) {
          setShowEditDialog(open);
          if (!open) {
            setEditingRecord(null);
            setEditFormData({});
          }
        }
      }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Submission</DialogTitle>
            <DialogDescription>
              Update fellowship candidate information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name *</Label>
                <Input
                  id="edit-name"
                  value={editFormData.name || ""}
                  onChange={(e) => handleEditFormChange("name", e.target.value)}
                  disabled={isUpdating}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-surname">Surname *</Label>
                <Input
                  id="edit-surname"
                  value={editFormData.surname || ""}
                  onChange={(e) => handleEditFormChange("surname", e.target.value)}
                  disabled={isUpdating}
                  className="rounded-xl"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-address">Home Address *</Label>
              <Input
                id="edit-address"
                value={editFormData.home_address || ""}
                onChange={(e) => handleEditFormChange("home_address", e.target.value)}
                disabled={isUpdating}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-gender">Gender *</Label>
              <Select
                value={editFormData.gender || ""}
                onValueChange={(value) => handleEditFormChange("gender", value)}
                disabled={isUpdating}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-contact">Contact Number *</Label>
              <Input
                id="edit-contact"
                value={editFormData.contact_number || ""}
                onChange={(e) => handleEditFormChange("contact_number", e.target.value)}
                disabled={isUpdating}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email Address</Label>
              <Input
                id="edit-email"
                type="email"
                value={editFormData.email || ""}
                onChange={(e) => handleEditFormChange("email", e.target.value)}
                disabled={isUpdating}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-area">Extension/Area *</Label>
              <Select
                value={editFormData.extension_area || ""}
                onValueChange={(value) => handleEditFormChange("extension_area", value)}
                disabled={isUpdating}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select area" />
                </SelectTrigger>
                <SelectContent>
                  {extensionAreas.map((area) => (
                    <SelectItem key={area} value={area}>
                      {area}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowEditDialog(false)}
              disabled={isUpdating}
              className="rounded-xl"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleUpdateSubmission}
              disabled={isUpdating}
              className="bg-[#063866] hover:bg-[#0B1B38] rounded-xl"
            >
              {isUpdating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={(open) => {
        if (!isDeleting) {
          setShowDeleteDialog(open);
          if (!open) setDeletingRecord(null);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the submission for{" "}
              <span className="font-semibold text-stone-900">
                {deletingRecord?.name} {deletingRecord?.surname}
              </span>
              . This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSubmission}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Dashboard;